import { useState as reactUseState, useCallback } from 'react';

export function useState(initialValue, options = {}) {
  const { onChange, validate, persist, key } = options;

  // Load persisted value if enabled
  const getInitialValue = () => {
    if (persist && key) {
      try {
        const stored = localStorage.getItem(key);
        if (stored !== null) {
          return JSON.parse(stored);
        }
      } catch (error) {
        console.warn('Failed to load persisted state:', error);
      }
    }
    return initialValue;
  };

  const [state, setState] = reactUseState(getInitialValue);

  // Enhanced setState with validation and callbacks
  const setValue = useCallback((value) => {
    setState(prevState => {
      const newValue = typeof value === 'function' ? value(prevState) : value;
      
      // Validation
      if (validate) {
        const validationResult = validate(newValue);
        if (validationResult === false) {
          console.warn('State validation failed for value:', newValue);
          return prevState;
        }
        if (typeof validationResult === 'string') {
          console.warn('State validation error:', validationResult);
          return prevState;
        }
      }

      // Persist to localStorage if enabled
      if (persist && key) {
        try {
          localStorage.setItem(key, JSON.stringify(newValue));
        } catch (error) {
          console.warn('Failed to persist state:', error);
        }
      }

      // Call onChange callback
      if (onChange) {
        onChange(newValue);
      }

      return newValue;
    });
  }, [onChange, validate, persist, key, setState]);

  // Reset function to restore initial value
  const reset = useCallback(() => {
    setValue(initialValue);
  }, [initialValue, setValue]);

  return [state, setValue, reset];
}

export default useState;
